import { Modal, Button, Spin } from "antd";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import Avvvatars from "avvvatars-react";
import {
  getAllStudentByClass,
  createAbsence,
  deleteAbsence,
  getAbsenceByClass,
  updateAbsence,
} from "../../../../api/etudiants";
import { createLate, deleteLate, getLateByClass } from "../../../../api/late";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../../../core/common/autorisations/permissionsMap";

type StudentStatus = "present" | "late" | "absent";

interface StudentState {
  id: string; // StudentProfil ID or Student ID
  studentId: string; // Student ID
  name: string;
  matricule: string;
  status: StudentStatus;
  originalStatus: StudentStatus;
  absenceIds: string[]; // ID from backend if exists
  lateIds: string[]; // ID from backend if exists
  selectedSessionIds: string[]; // sessions impactées
  originalSelectedSessionIds: string[]; // sessions impactées originales
  lateMinutes?: number; // minutes de retard (pour "late")
  originalLateMinutes?: number;
}

type Props = {
  visible: boolean;
  idsCourseVersionEDT: any;
  sessions: any[];
  idsClass: any[];
  onCancel: () => void;
};

const AbsenceCourseModal = ({
  visible,
  idsCourseVersionEDT,
  sessions,
  idsClass,
  onCancel,
}: Props) => {
  const [students, setStudents] = useState<StudentState[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Status cycling: Present -> Late -> Absent -> Present
  const nextStatus: Record<StudentStatus, StudentStatus> = {
    present: "absent",
    absent: "late",
    late: "present",
  };

  const statusColors: Record<StudentStatus, string> = {
    present: "border-success",
    late: "border-warning",
    absent: "border-danger",
  };

  const fetchData = async () => {
    if (
      idsClass.length === 0 ||
      !idsCourseVersionEDT ||
      idsCourseVersionEDT.length === 0
    )
      return;

    setLoading(true);
    try {
      const idCourse = idsCourseVersionEDT[0]; // Use the first ID for reference
      // 1. Fetch all students in the class
      const studentsResponses = await Promise.all(
        idsClass.map((Class: any) =>
          getAllStudentByClass(
            Class?.id,
            buildPermissionContext(Resource.STUDENT, Action.READ),
          ),
        ),
      );
      const allStudents = studentsResponses
        .map((response: any) => response.data || [])
        .flat();
      // console.log("allStudents: ", allStudents);

      // 2. Fetch existing absences
      const absencesResponse = await Promise.all(
        idsClass.map((Class: any) =>
          getAbsenceByClass(
            Class?.id,
            idCourse,
            buildPermissionContext(Resource.STUDENT_ABSENCE, Action.READ),
          ),
        ),
      );
      const absences = absencesResponse
        .map((response: any) => response.data || [])
        .flat();

      // 3. Fetch existing lates
      const latesResponse = await Promise.all(
        idsClass.map((Class: any) =>
          getLateByClass(
            Class?.id,
            idCourse,
            buildPermissionContext(Resource.STUDENT_LATE, Action.READ),
          ),
        ),
      );
      const lates = latesResponse
        .map((response: any) => response.data || [])
        .flat();

      // 4. Merge data
      const mergedStudents: StudentState[] = allStudents.map((student: any) => {
        const studentProfilId = student.StudentProfil?.id;

        // Check for absence
        const absence = absences.filter(
          (a: any) =>
            a.StudentProfil?.id === studentProfilId &&
            idsCourseVersionEDT.includes(a.id_courseVersionEDT),
        );
        // Check for late (récupérer tous les retards correspondants à l'étudiant sans doublon)
        const late = lates
          .filter(
            (l: any) =>
              l.StudentProfil?.id === studentProfilId &&
              idsCourseVersionEDT.includes(l.id_courseVersionEDT),
          )
          .filter(
            (value, index, self) =>
              index === self.findIndex((t) => t.id === value.id),
          );

        let status: StudentStatus = "present";
        let absenceIds: string[] = [];
        let lateIds: string[] = [];
        let selectedSessionIds: string[] = [];
        let lateMinutes: number | undefined = undefined;

        if (absence.length > 0) {
          // console.log("Absence: ", absence);
          status = "absent";
          absenceIds = absence.map((a: any) => a.id);
          selectedSessionIds = absence
            .map((a: any) => a.id_session || [])
            .flat();
        } else if (late.length > 0) {
          // console.log("Late: ", late);
          status = "late";
          lateIds = late.map((l: any) => l.id);
          selectedSessionIds = late.map((l: any) => l.id_session || []).flat();
          // calculer les minutes de retard à partir du temps sous la forme "HH:MM::SS"
          lateMinutes = late[0]?.time
            ? parseInt(late[0]?.time.split(":")[0], 10) * 60 +
              parseInt(late[0]?.time.split(":")[1], 10)
            : undefined;
        }

        return {
          id: student.id,
          studentId: studentProfilId,
          name: `${student.StudentProfil?.firstname} ${student.StudentProfil?.lastname}`,
          matricule: student.StudentProfil?.matricule,
          status: status,
          originalStatus: status,
          absenceIds: absenceIds,
          lateIds: lateIds,
          originalSelectedSessionIds: selectedSessionIds,
          selectedSessionIds: selectedSessionIds,
          lateMinutes: lateMinutes,
          originalLateMinutes: lateMinutes,
        };
      });

      setStudents(mergedStudents);
    } catch (error: any) {
      // console.error("Erreur chargement données:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors du chargement des données.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      // console.log("Sessions: ",sessions)
      fetchData();
    }
  }, [visible, idsClass, idsCourseVersionEDT]);

  const handleStudentClick = (studentIndex: number) => {
    setStudents((prev) => {
      const newStudents = [...prev];
      const currentStudent = newStudents[studentIndex];
      const next = nextStatus[currentStudent.status];
      newStudents[studentIndex] = {
        ...currentStudent,
        status: next,
      };
      // Auto-select all sessions when switching to absent or late if none selected
      if (
        (next === "absent" || next === "late") &&
        currentStudent.selectedSessionIds.length === 0
      ) {
        newStudents[studentIndex].selectedSessionIds = sessions.map(
          (s) => s.id,
        );
      }
      return newStudents;
    });
  };

  const handleSave = async () => {
    setSaving(true);

    // Utilitaire pour comparer les tableaux de sessions
    const areSessionsEqual = (a: string[], b: string[]) =>
      a.length === b.length && a.every((id) => b.includes(id));

    try {
      const idCourse = idsCourseVersionEDT[0];
      //const promises: Promise<any>[] = [];
      const cleanupPromises: Promise<any>[] = [];
      const mutationPromises: Promise<any>[] = [];

      // 1. Vérifier s'il reste au moins une anomalie (absent ou retard)
      const hasAnyAbsenceOrLate = students.some(
        (s) => s.status === "absent" || s.status === "late",
      );

      // CAS : Tout le monde est présent
      if (!hasAnyAbsenceOrLate) {
        // On nettoie d'abord tous les anciens enregistrements existants
        for (const student of students) {
          if (student.absenceIds?.length > 0) {
            cleanupPromises.push(
              ...student.absenceIds.map((id) =>
                deleteAbsence(
                  id,
                  buildPermissionContext(
                    Resource.STUDENT_ABSENCE,
                    Action.DELETE,
                  ),
                ),
              ),
            );
          }
          if (student.lateIds?.length > 0) {
            cleanupPromises.push(
              ...student.lateIds.map((id) =>
                deleteLate(
                  id,
                  buildPermissionContext(Resource.STUDENT_LATE, Action.DELETE),
                ),
              ),
            );
          }
        }
        // Une fois le nettoyage ajouté aux promesses, on crée le statut particulier
        await createAbsence(
          {
            id_studentProfil: null,
            id_courseVersionEDT: idCourse,
            id_sessions: sessions.map((s) => s.id),
            all_present: true,
          },
          buildPermissionContext(Resource.STUDENT_ABSENCE, Action.CREATE),
        );
      } else {
        // 2. TRAITEMENT PAR ÉTUDIANT
        for (let i = 0; i < students.length; i++) {
          const student = students[i];

          // Vérification simplifiée : statut ou sessions modifiés
          const isStatusUnchanged = student.status === student.originalStatus;
          const isSessionsUnchanged = areSessionsEqual(
            student.selectedSessionIds,
            student.originalSelectedSessionIds,
          );

          if (isStatusUnchanged && isSessionsUnchanged) continue;

          // --- PHASE DE NETTOYAGE ---
          // Si l'étudiant était absent et ne l'est plus (ou a changé pour retard)
          if (
            student.originalStatus === "absent" &&
            student.status !== "absent"
          ) {
            if (student.absenceIds?.length > 0) {
              cleanupPromises.push(
                ...student.absenceIds.map((id) =>
                  deleteAbsence(
                    id,
                    buildPermissionContext(
                      Resource.STUDENT_ABSENCE,
                      Action.DELETE,
                    ),
                  ),
                ),
              );
            }
          }

          // Si l'étudiant était en retard et ne l'est plus (ou a changé pour absent)
          if (student.originalStatus === "late" && student.status !== "late") {
            if (student.lateIds?.length > 0) {
              cleanupPromises.push(
                ...student.lateIds.map((id) =>
                  deleteLate(
                    id,
                    buildPermissionContext(
                      Resource.STUDENT_LATE,
                      Action.DELETE,
                    ),
                  ),
                ),
              );
            }
          }

          // --- PHASE DE CRÉATION / MISE À JOUR ---
          const sessionsToSave =
            student.selectedSessionIds.length > 0
              ? student.selectedSessionIds
              : sessions.map((s) => s.id);

          if (student.status === "absent") {
            if (
              student.originalStatus === "absent" &&
              student.absenceIds?.length > 0
            ) {
              // Mise à jour de l'absence existante
              mutationPromises.push(
                ...student.absenceIds.map((id) =>
                  updateAbsence(
                    id,
                    {
                      id_studentProfil: student.studentId,
                      id_courseVersionEDT: idCourse,
                      id_sessions: sessionsToSave,
                    },
                    buildPermissionContext(
                      Resource.STUDENT_ABSENCE,
                      Action.UPDATE,
                    ),
                  ),
                ),
              );
            } else {
              // Nouvelle absence
              mutationPromises.push(
                createAbsence(
                  {
                    id_studentProfil: student.studentId,
                    id_courseVersionEDT: idCourse,
                    id_sessions: sessionsToSave,
                  },
                  buildPermissionContext(
                    Resource.STUDENT_ABSENCE,
                    Action.CREATE,
                  ),
                ),
              );
            }
          } else if (student.status === "late") {
            // Pour les retards, on crée une entrée par session sélectionnée
            mutationPromises.push(
              createLate(
                {
                  id_studentProfil: student.studentId,
                  id_courseVersionEDT: idCourse,
                  id_sessions: sessionsToSave,
                  // Le champ "time" est retiré ici
                },
                buildPermissionContext(Resource.STUDENT_LATE, Action.CREATE),
              ),
            );
          }
        }
      }

      // 3. Exécution
      // if (promises.length > 0) {
      //   await Promise.all(promises);
      // }

      // on nettoie d'abord (suppressions),
      // puis seulement après on crée/met à jour, pour éviter que la vérification "absence déjà existante" côté backend ne se déclenche
      // avant que l'ancienne absence ait été réellement supprimée.
      if (cleanupPromises.length > 0) {
        await Promise.all(cleanupPromises);
      }
      if (mutationPromises.length > 0) {
        await Promise.all(mutationPromises);
      }
      toast.success("Enregistrement réussi");
      fetchData();
      onCancel();
    } catch (error: any) {
      // console.error("Erreur sauvegarde:", error);
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const getStats = () => {
    const present = students.filter((s) => s.status === "present").length;
    const late = students.filter((s) => s.status === "late").length;
    const absent = students.filter((s) => s.status === "absent").length;
    return { present, late, absent };
  };

  const stats = getStats();

  return (
    <Modal
      title="Gestion des absences"
      open={visible}
      centered={true}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Fermer
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={saving}
          onClick={handleSave}
        >
          Enregistrer
        </Button>,
      ]}
      destroyOnClose
    >
      <div className="mb-4">
        <div className="d-flex gap-3 justify-content-center">
          <div className="badge bg-success-transparent p-2 px-3">
            <span className="fs-14">Présents: {stats.present}</span>
          </div>
          <div className="badge bg-warning-transparent p-2 px-3">
            <span className="fs-14">Retards: {stats.late}</span>
          </div>
          <div className="badge bg-danger-transparent p-2 px-3">
            <span className="fs-14">Absents: {stats.absent}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center p-5">
          <Spin size="large" />
        </div>
      ) : students.length === 0 ? (
        <div className="text-center p-5 text-muted">
          Aucun étudiant trouvé dans cette classe.
        </div>
      ) : (
        <div
          className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-5 g-2"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          {students.map((student, index) => (
            <div key={student.id} className="col">
              <div
                className={`card h-100 cursor-pointer user-select-none border-2 ${
                  statusColors[student.status]
                } shadow-sm`}
                onClick={() => handleStudentClick(index)}
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s",
                  backgroundColor:
                    student.status === "absent"
                      ? "rgba(255, 0, 0, 0.1)"
                      : student.status === "late"
                        ? "rgba(255, 255, 0, 0.1)"
                        : "",
                }}
              >
                <div className="card-body p-2 d-flex flex-column align-items-center justify-content-center text-center">
                  <div className="mb-1">
                    <Avvvatars
                      value={student.name}
                      style="character"
                      size={40}
                    />
                  </div>
                  <h6
                    className="fw-bold mb-1 text-truncate w-100"
                    style={{ fontSize: "0.85rem" }}
                    title={student.name}
                  >
                    {student.name}
                  </h6>
                  <p
                    className="text-muted mb-0"
                    style={{ fontSize: "0.75rem" }}
                  >
                    {student.matricule}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default AbsenceCourseModal;
